import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Reusable "attention" mechanism.
//
// Any feature can raise a globally visible attention pop-up via the
// `raiseAttention` callback, no matter which view the user is currently on.
// The mechanism is intentionally generic so other attention views (not just
// pizza) can plug into it:
//
//   const { raiseAttention } = useAttention();
//   raiseAttention({
//     key: 'pizza:<id>:<serveTs>',        // stable, de-duplicated identifier
//     title: 'Pizza gleich fällig',
//     message: 'Max Mustermann (#1234) – 18:30',
//     severity: 'warning' | 'error',
//     primaryAction: { label: 'Zur Küche', to: '/kitchen' },
//   });
//
// An alert with a given `key` only ever pops once. Use `releaseAttention` to
// forget keys that are no longer relevant so they may trigger again later.

const AttentionContext = createContext({
  raiseAttention: () => {},
  dismissAttention: () => {},
  releaseAttention: () => {},
});

export const useAttention = () => useContext(AttentionContext);

const AttentionPopupHost = ({ alert, remaining, onDismiss, onAction }) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!alert) return null;

  const targetPath = alert.primaryAction?.to;
  const alreadyOnTarget = targetPath && location?.pathname === targetPath;

  const handleAction = () => {
    if (targetPath && !alreadyOnTarget) navigate(targetPath);
    onAction(alert.key);
  };

  return (
    <Dialog open onClose={() => onDismiss(alert.key)} maxWidth='xs' fullWidth>
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <WarningAmberIcon color={alert.severity === 'error' ? 'error' : 'warning'} />
          <Box component='span'>{alert.title}</Box>
          {remaining > 0 && (
            <Chip size='small' color='warning' label={`+${remaining} weitere`} sx={{ ml: 'auto' }} />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant='body1'>{alert.message}</Typography>
        {alert.detail && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            {alert.detail}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onDismiss(alert.key)}>Ausblenden</Button>
        {alert.primaryAction && (
          <Button onClick={handleAction} variant='contained' color={alert.severity === 'error' ? 'error' : 'primary'}>
            {alreadyOnTarget ? 'OK' : alert.primaryAction.label}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const AttentionProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  // Keys that have already been surfaced (or explicitly dismissed) so we never
  // re-pop the same event on subsequent polls.
  const seenKeysRef = useRef(new Set());

  const raiseAttention = useCallback(alert => {
    if (!alert?.key) return;
    if (seenKeysRef.current.has(alert.key)) return;
    seenKeysRef.current.add(alert.key);
    setQueue(prev => [...prev, alert]);
  }, []);

  const dismissAttention = useCallback(key => {
    setQueue(prev => prev.filter(a => a.key !== key));
  }, []);

  // Forget previously-seen keys matching `predicate(key)` so they can be raised
  // again later (e.g. when an item leaves and re-enters its attention window).
  const releaseAttention = useCallback(predicate => {
    if (typeof predicate !== 'function') return;
    let changed = false;
    for (const key of Array.from(seenKeysRef.current)) {
      if (predicate(key)) {
        seenKeysRef.current.delete(key);
        changed = true;
      }
    }
    if (changed) {
      setQueue(prev => prev.filter(a => !predicate(a.key)));
    }
  }, []);

  const value = useMemo(
    () => ({ raiseAttention, dismissAttention, releaseAttention }),
    [raiseAttention, dismissAttention, releaseAttention]
  );

  const currentAlert = queue[0] || null;

  return (
    <AttentionContext.Provider value={value}>
      {children}
      <AttentionPopupHost
        alert={currentAlert}
        remaining={Math.max(0, queue.length - 1)}
        onDismiss={dismissAttention}
        onAction={dismissAttention}
      />
    </AttentionContext.Provider>
  );
};

export default AttentionContext;
